<template>
    <div class="basic-tabs">
        <div
            class="basic-tabs-headers flex flex-nowrap mb-[30px]"
            role="tablist"
        >
            <component
                :is="basicTab.component"
                v-for="basicTab in computedBasicTabsHeaders"
                v-bind="basicTab"
                :id="basicTab.id"
                :key="basicTab.id"
                @click="selectTab(basicTab.value)"
            ></component>
        </div>
        <div data-testid="basic-tab-content">
            <!-- Without :key attribute view were not updating after changing activeTabContentComponent value -->
            <BasicTransition mode="out-in">
                <KeepAlive :max="10">
                    <component
                        :is="activeTabContentComponent"
                        v-if="activeTabContentComponent"
                        :id="`${selectedTab}-tab-content`"
                        :key="selectedTab"
                        :aria-labelledby="`${selectedTab}-tab-header`"
                        role="tabpanel"
                    ></component>
                </KeepAlive>
            </BasicTransition>
        </div>
    </div>
</template>

<script setup lang="ts">
import BasicTransition from '@atoms/BasicTransition.vue';
import type { VNode, VNodeTypes } from 'vue';

const emits = defineEmits<{
    (e: 'update:modelValue', value: string): void;
}>();

export interface BasicTabsInterface {
    modelValue?: string;
    grow?: boolean;
}

export interface BasicTabsSlots {
    default(): VNode[];
}

const props = defineProps<BasicTabsInterface>();

const slots = defineSlots<BasicTabsSlots>();

type CustomVNodeTypes = {
    __name: string;
} & VNodeTypes;

const defaultSlotContent = slots.default!();

const basicTabsHeaders = shallowRef(
    defaultSlotContent.filter(
        (node) => (node.type as CustomVNodeTypes).__name === 'BasicTabHeader'
    )
);

const selectedTab: Ref<string> = ref(
    props.modelValue || basicTabsHeaders.value[0].props!.value
);

const computedBasicTabsHeaders = computed(() => {
    return basicTabsHeaders.value.map((basicTab) => {
        const value = basicTab.props!.value;
        return {
            id: `${value}-tab-header`,
            value,
            grow: props.grow,
            isActive: value === selectedTab.value,
            component: basicTab,
        };
    });
});

const tabsContent = shallowRef(
    defaultSlotContent.filter(
        (node) =>
            typeof node.type !== 'symbol' &&
            (node.type as CustomVNodeTypes).__name !== 'BasicTabHeader'
    )
);

const activeTabContentComponent = computed(() =>
    tabsContent.value.find(
        (tabContent) => tabContent.props!.value === selectedTab.value
    )
) as ComputedRef<VNode>;

watch(
    () => props.modelValue,
    () => {
        if (props.modelValue) {
            selectedTab.value = props.modelValue;
        }
    }
);

const selectTab = (tabValue: string) => {
    if (selectedTab.value === tabValue) return;
    selectedTab.value = tabValue;
    emits('update:modelValue', tabValue);
};
</script>
